import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTelegram } from './TelegramProvider';
import { useTon } from './TelegramTonProvider';
import { useGameBalance } from './hooks/useGameBalance';
import { useMultiplier } from './hooks/useMultiplier';
import { useSounds } from './hooks/useSounds';
import { formatLilape } from './config/gameConfig';
import { DepositWithdrawPanel } from './components/DepositWithdrawPanel';

/* ── Config ── */
const WIN_RATE = 0.03; // 3% win rate
const WIN_MULT = 10; // x10 base (x30 with Gold VIP)
const BET_OPTIONS = [20_000_000, 50_000_000, 100_000_000, 500_000_000]; // 20M, 50M, 100M, 500M
const FLIP_MS = 1200;

/* ── Coin face ── */
const FACES = {
  ape:    { icon: '🦍', label: 'APE',    color: '#FFD700', glow: 'rgba(255,215,0,0.6)'  },
  banana: { icon: '🍌', label: 'BANANA', color: '#FFD60A', glow: 'rgba(255,214,10,0.5)' },
};

export function CoinFlip({ onBack }) {
  const { hapticFeedback } = useTelegram();
  const { connected, sendTransaction, address } = useTon();
  const gameBalanceHook = useGameBalance(address, sendTransaction);
  const { gameBalance, useSpin, addWinnings, canSpin } = gameBalanceHook;
  const { applyMultiplier } = useMultiplier();
  const { play, spinSound } = useSounds();

  const [bet, setBet] = useState(BET_OPTIONS[0]);
  const [choice, setChoice] = useState('ape');
  const [flipping, setFlipping] = useState(false);
  const [result, setResult] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [coinFace, setCoinFace] = useState('ape');
  const [winAmount, setWinAmount] = useState(0);
  const [showWallet, setShowWallet] = useState(false);
  const [history, setHistory] = useState([]);

  const flipInterval = useRef(null);

  useEffect(() => () => clearInterval(flipInterval.current), []);

  const flip = useCallback(async () => {
    if (flipping) return;
    if (!canSpin(bet)) {
      hapticFeedback?.('error');
      return;
    }

    // Deduct bet (server-side)
    const betSuccess = await useSpin(bet, 'coinflip');
    if (!betSuccess) return;

    hapticFeedback?.('medium');
    spinSound();
    setFlipping(true);
    setResult(null);
    setOutcome(null);

    const faces = ['ape', 'banana'];
    let i = 0;
    flipInterval.current = setInterval(() => setCoinFace(faces[i++ % 2]), 80);

    setTimeout(() => {
      clearInterval(flipInterval.current);
      // 3% win rate
      const won = Math.random() < WIN_RATE;
      const landed = won ? choice : (choice === 'ape' ? 'banana' : 'ape');
      const baseGain = won ? Math.round(bet * WIN_MULT) : 0;
      const gain = applyMultiplier(baseGain);

      setCoinFace(landed);
      setResult(landed);
      setOutcome(won ? 'win' : 'lose');
      setWinAmount(gain);

      if (gain > 0) {
        addWinnings(gain, 'coinflip', bet);
      }

      setFlipping(false);
      play(won ? (gain > bet * 3 ? 'bigWin' : 'win') : 'lose');
      hapticFeedback?.(won ? 'success' : 'error');
      setHistory(h => [{ result: landed, won, bet, gain }, ...h].slice(0, 6));
    }, FLIP_MS);
  }, [flipping, bet, choice, canSpin, useSpin, addWinnings, applyMultiplier, hapticFeedback, play, spinSound]);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%)',
      padding: '16px',
      paddingBottom: '100px',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
        <button onClick={onBack} style={{
          background: 'rgba(255,255,255,0.1)',
          border: 'none',
          borderRadius: '12px',
          padding: '10px 16px',
          color: '#fff',
          fontSize: '16px',
          cursor: 'pointer',
        }}>← Back</button>
        <h1 style={{
          flex: 1,
          textAlign: 'center',
          color: '#FFD700',
          fontSize: '24px',
          margin: 0,
        }}>🪙 Coin Flip</h1>
      </div>

      {/* Balance */}
      <div style={{
        background: 'rgba(255,215,0,0.1)',
        border: '1px solid rgba(255,215,0,0.3)',
        borderRadius: '16px',
        padding: '16px',
        textAlign: 'center',
        marginBottom: '20px',
      }}>
        <div style={{ color: '#888', fontSize: '12px' }}>GAME BALANCE</div>
        <div style={{ color: '#FFD700', fontSize: '28px', fontWeight: 'bold', marginBottom: 8 }}>
          {formatLilape(gameBalance)} LILAPE
        </div>
        <button onClick={() => setShowWallet(true)} style={{
          padding: '8px 20px', borderRadius: 8,
          background: 'linear-gradient(135deg, #10B981, #059669)',
          border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
        }}>💰 Deposit / Withdraw</button>
      </div>

      {showWallet && <DepositWithdrawPanel onClose={() => setShowWallet(false)} gameBalanceHook={gameBalanceHook} />}

      {/* Coin */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '24px',
      }}>
        <div style={{
          width: '140px',
          height: '140px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${FACES[coinFace].color} 0%, ${FACES[coinFace].color}88 100%)`,
          boxShadow: `0 0 40px ${FACES[coinFace].glow}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '64px',
          animation: flipping ? 'spin 0.15s linear infinite' : 'none',
        }}>
          {FACES[coinFace].icon}
        </div>
      </div>

      {/* Result */}
      {outcome && (
        <div style={{
          textAlign: 'center',
          marginBottom: '20px',
          padding: '16px',
          borderRadius: '12px',
          background: outcome === 'win' ? 'rgba(0,255,0,0.1)' : 'rgba(255,0,0,0.1)',
        }}>
          <div style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: outcome === 'win' ? '#00ff00' : '#ff4444',
          }}>
            {outcome === 'win' ? `🎉 WIN +${formatLilape(winAmount)}` : '😢 LOSE'}
          </div>
        </div>
      )}

      {/* Choice */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px', textAlign: 'center' }}>
          PICK YOUR SIDE
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          {Object.entries(FACES).map(([key, face]) => (
            <button
              key={key}
              onClick={() => !flipping && setChoice(key)}
              style={{
                flex: 1,
                padding: '16px',
                borderRadius: '12px',
                border: choice === key ? `2px solid ${face.color}` : '2px solid rgba(255,255,255,0.1)',
                background: choice === key ? `${face.color}22` : 'rgba(255,255,255,0.05)',
                color: '#fff',
                fontSize: '20px',
                cursor: 'pointer',
                opacity: flipping ? 0.5 : 1,
              }}
            >
              {face.icon} {face.label}
            </button>
          ))}
        </div>
      </div>

      {/* Bet Options */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px', textAlign: 'center' }}>
          BET AMOUNT
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {BET_OPTIONS.map(opt => (
            <button
              key={opt}
              onClick={() => !flipping && setBet(opt)}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: bet === opt ? '2px solid #FFD700' : '1px solid rgba(255,255,255,0.2)',
                background: bet === opt ? 'rgba(255,215,0,0.2)' : 'rgba(255,255,255,0.05)',
                color: bet === opt ? '#FFD700' : '#fff',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                opacity: flipping ? 0.5 : 1,
              }}
            >
              {formatLilape(opt)}
            </button>
          ))}
        </div>
      </div>

      {/* Flip Button */}
      <button
        onClick={flip}
        disabled={flipping || !canSpin(bet)}
        style={{
          width: '100%',
          padding: '18px',
          borderRadius: '14px',
          border: 'none',
          background: flipping ? '#555' : canSpin(bet) ? 'linear-gradient(135deg, #FFD700, #FFA500)' : '#333',
          color: '#000',
          fontSize: '18px',
          fontWeight: 'bold',
          cursor: flipping || !canSpin(bet) ? 'not-allowed' : 'pointer',
        }}
      >
        {flipping ? '🪙 FLIPPING...' : `FLIP ${formatLilape(bet)}`}
      </button>

      {/* History */}
      {history.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px' }}>HISTORY</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {history.map((h, i) => (
              <div key={i} style={{
                padding: '8px 12px',
                borderRadius: '8px',
                background: h.won ? 'rgba(0,255,0,0.1)' : 'rgba(255,0,0,0.1)',
                border: `1px solid ${h.won ? 'rgba(0,255,0,0.3)' : 'rgba(255,0,0,0.3)'}`,
                fontSize: '20px',
              }}>
                {FACES[h.result].icon}
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotateY(0deg); }
          to { transform: rotateY(180deg); }
        }
      `}</style>
    </div>
  );
}
